// =============================================================================
// SimSense Marketplace — Discovery Storefront
// =============================================================================
// OT-278: Main marketplace browse/search page with three-lane layout.
// Feedback pass: responsive partner grid, toggle price pills, track dropdown,
// "This Week" section, weighted rating prep.

import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMarketplaceBrowse, useSetupOfTheWeek, useRisingCreators } from "../../hooks/useMarketplace";
import { useAuth } from "../../hooks/useAuth";
import { useWishlist } from "../../hooks/useWishlist";
import { SetupCard } from "./components/SetupCard";
import { SearchAutocomplete } from "./components/SearchAutocomplete";
import { formatLapTime } from "../../utils/formatters";
import type { MarketplaceFilters } from "../../types/marketplace";
import "./MarketplaceDiscovery.css";

// Mock partner shops — will be replaced by API data
const PARTNER_SHOPS = [
  { slug: "craig-setup-shop", name: "Craig Setup Shop", setups: 18, initial: "CSS" },
  { slug: "maconi-setup-shop", name: "Maconi", setups: 12, initial: "M" },
  { slug: "outlier-speed-co", name: "Outlier Speed Co.", setups: 24, initial: "OSC" },
]; // Alphabetical to avoid favoritism

const LANE_FILTERS = [
  { value: "all", label: "All" },
  { value: "shop", label: "Shops" },
  { value: "pro_builder", label: "Verified Creators" },
  { value: "community", label: "Community" },
] as const;

// Bayesian weighted rating: (v/(v+m))*R + (m/(v+m))*C
// v = reviewCount, m = minimum reviews to trust, R = setup rating, C = global avg
function weightedRating(rating: number, reviewCount: number, globalAvg = 4.5, minReviews = 10): number {
  return (reviewCount / (reviewCount + minReviews)) * rating +
    (minReviews / (reviewCount + minReviews)) * globalAvg;
}

export function MarketplaceDiscovery() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  // TODO: Replace with real creator role check when backend lands
  const isCreator = isAdmin; // For now, admin = has workshop access
  const [filters, setFilters] = useState<MarketplaceFilters>({
    search: "",
    showFree: false,
    showPremium: false,
    laneFilter: "all",
    season: "2026S2",
    track: "",
    discipline: "",
  });

  const { setups, loading, availableTracks } = useMarketplaceBrowse(filters);
  const { sotw } = useSetupOfTheWeek();
  const { creators: risingCreators } = useRisingCreators();
  const { isSaved, toggleSave, savedCount } = useWishlist();
  const [showSaved, setShowSaved] = useState(false);

  const handleInstall = (id: string) => {
    console.log(`Installing setup ${id}`);
  };

  const handleViewDetails = (id: string) => {
    navigate(`/marketplace/setup/${id}`);
  };

  // Determine if any filter is active (triggers flat results view)
  const priceActive = filters.showFree !== filters.showPremium; // one but not both
  const isFiltering = !!(filters.search || filters.laneFilter !== "all" || priceActive || filters.track || filters.discipline || showSaved);

  // Apply saved filter
  const displaySetups = showSaved ? setups.filter(s => isSaved(s.id)) : setups;

  // Section splits for default browse
  const trendingSetups = useMemo(
    () => [...setups].sort((a, b) => b.downloadCount - a.downloadCount).slice(0, 4),
    [setups]
  );
  const topRated = useMemo(
    () => [...setups]
      .filter((s) => s.reviewCount >= 3) // lower threshold for mock data
      .sort((a, b) => weightedRating(b.rating, b.reviewCount) - weightedRating(a.rating, a.reviewCount))
      .slice(0, 4),
    [setups]
  );
  const communitySetups = useMemo(() => setups.filter((s) => s.lane === "community"), [setups]);
  const proBuilderSetups = useMemo(() => setups.filter((s) => s.lane === "pro_builder"), [setups]);

  const togglePrice = (which: "free" | "premium") => {
    setFilters((f) => ({
      ...f,
      [which === "free" ? "showFree" : "showPremium"]:
        !f[which === "free" ? "showFree" : "showPremium"],
    }));
  };

  return (
    <div className="marketplace-page">
      {/* Hero Section */}
      <section className="marketplace-hero">
        <div className="hero-content">
          <h1>Setup Marketplace</h1>
          <p>
            Discover telemetry-proven setups from top creators and partner
            shops. Every setup shows its real lap time.
          </p>
        </div>
        {isCreator ? (
          <Link to="/marketplace/dashboard" className="hero-workshop-btn">
            Creator Workshop
          </Link>
        ) : (
          <Link to="/marketplace/sell" className="hero-workshop-btn hero-sell-btn">
            Post Setups
          </Link>
        )}
      </section>

      {/* Partner Shops — Responsive Grid (alphabetical) */}
      <section className="partner-shops-section">
        <h2 className="section-title-sm">Partner Shops</h2>
        <div className="partner-shops-grid">
          {PARTNER_SHOPS.map((shop) => (
            <Link
              key={shop.slug}
              to={`/marketplace/shop/${shop.slug}`}
              className="partner-shop-card"
            >
              <div className="partner-shop-logo">{shop.initial}</div>
              <div className="partner-shop-info">
                <span className="partner-shop-name">{shop.name}</span>
                <span className="partner-shop-count">{shop.setups} setups</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Global Search & Filter Bar */}
      <div className="marketplace-filters sticky">
        <SearchAutocomplete
          value={filters.search}
          onChange={(v) => setFilters((f) => ({ ...f, search: v }))}
          setups={setups}
        />
        <div className="filter-row">
          {/* Lane pills */}
          <div className="filter-pills">
            {LANE_FILTERS.map((f) => (
              <button
                key={f.value}
                className={`pill ${filters.laneFilter === f.value ? "active" : ""}`}
                onClick={() => setFilters((prev) => ({ ...prev, laneFilter: f.value as any }))}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Price toggles + Track dropdown + Season */}
          <div className="filter-pills">
            <button
              className={`pill pill-price pill-free ${filters.showFree ? "active" : ""}`}
              onClick={() => togglePrice("free")}
            >
              Free
            </button>
            <button
              className={`pill pill-price pill-premium ${filters.showPremium ? "active" : ""}`}
              onClick={() => togglePrice("premium")}
            >
              Paid
            </button>
            {savedCount > 0 && (
              <button
                className={`pill pill-saved ${showSaved ? "active" : ""}`}
                onClick={() => setShowSaved(v => !v)}
              >
                Saved ({savedCount})
              </button>
            )}

            <select
              className="dropdown-filter"
              value={filters.discipline}
              onChange={(e) =>
                setFilters((f) => ({ ...f, discipline: e.target.value }))
              }
            >
              <option value="">All Disciplines</option>
              <option value="road">Road</option>
              <option value="oval">Oval</option>
              <option value="dirt_road">Dirt Road</option>
              <option value="dirt_oval">Dirt Oval</option>
            </select>

            <select
              className="dropdown-filter"
              value={filters.track}
              onChange={(e) =>
                setFilters((f) => ({ ...f, track: e.target.value }))
              }
            >
              <option value="">All Tracks</option>
              {availableTracks.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            <select
              className="dropdown-filter"
              value={filters.season}
              onChange={(e) =>
                setFilters((f) => ({ ...f, season: e.target.value }))
              }
            >
              <option value="2026S2">2026 Season 2</option>
              <option value="2026S1">2026 Season 1</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="marketplace-loading">Loading setups...</div>
      ) : isFiltering ? (
        /* Filtered/Search Results — flat list */
        <section className="marketplace-section">
          <h2 className="section-title">
            {showSaved ? `Saved Setups (${displaySetups.length})` : filters.search ? `Search Results (${displaySetups.length})` : `Setups (${displaySetups.length})`}
          </h2>
          {displaySetups.length > 0 ? (
            <div className="setup-grid">
              {displaySetups.map((setup) => (
                <SetupCard
                  key={setup.id}
                  setup={setup}
                  onInstall={handleInstall}
                  onViewDetails={handleViewDetails}
                  isSaved={isSaved(setup.id)}
                  onToggleSave={toggleSave}
                />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              No setups found matching your filters.
            </div>
          )}
        </section>
      ) : (
        /* Default Browse — Sectioned layout */
        <>
          {/* Setup of the Week Spotlight */}
          {sotw && (
            <section className="sotw-spotlight" onClick={() => handleViewDetails(sotw.setup.id)}>
              <div className="sotw-badge">SETUP OF THE WEEK</div>
              <div className="sotw-content">
                <div className="sotw-info">
                  <h2 className="sotw-title">{sotw.setup.setupName}</h2>
                  <p className="sotw-car">{sotw.setup.carName} — {sotw.setup.trackName}</p>
                  <p className="sotw-note">{sotw.editorNote}</p>
                  <div className="sotw-meta">
                    <span className="sotw-creator">
                      By {sotw.setup.creatorName}
                      {sotw.setup.isVerified && <span className="sotw-verified">✓</span>}
                    </span>
                    <span className="sotw-stars">{"★".repeat(Math.floor(sotw.setup.rating))} {sotw.setup.rating.toFixed(1)}</span>
                    <span className="sotw-reviews">({sotw.setup.reviewCount} reviews)</span>
                  </div>
                </div>
                <div className="sotw-proof">
                  <div className="sotw-proof-label">PROVEN LAP TIME</div>
                  <div className="sotw-proof-time">{formatLapTime(sotw.setup.bestLapTime)}</div>
                  <div className="sotw-proof-downloads">{sotw.setup.downloadCount.toLocaleString()} downloads</div>
                  <div className={`sotw-price ${sotw.setup.isFree ? "sotw-price-free" : ""}`}>
                    {sotw.setup.isFree ? "FREE" : `$${(sotw.setup.priceCents / 100).toFixed(2)}`}
                  </div>
                </div>
              </div>
              <div className="sotw-week">{sotw.weekLabel}</div>
            </section>
          )}

          {/* Trending This Week */}
          {trendingSetups.length > 0 && (
            <section className="marketplace-section">
              <h2 className="section-title">Trending This Week</h2>
              <div className="setup-grid">
                {trendingSetups.map((setup) => (
                  <SetupCard
                    key={setup.id}
                    setup={setup}
                    onInstall={handleInstall}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Top Rated (weighted) */}
          {topRated.length > 0 && (
            <section className="marketplace-section">
              <div className="section-header">
                <h2 className="section-title">Top Rated</h2>
                <span className="section-subtitle">Weighted by review count</span>
              </div>
              <div className="setup-grid">
                {topRated.map((setup) => (
                  <SetupCard
                    key={setup.id}
                    setup={setup}
                    onInstall={handleInstall}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Verified Creator Setups */}
          {proBuilderSetups.length > 0 && (
            <section className="marketplace-section">
              <div className="section-header">
                <h2 className="section-title">Verified Creator Picks</h2>
                <button
                  className="see-all-btn"
                  onClick={() => setFilters((f) => ({ ...f, laneFilter: "pro_builder" }))}
                >
                  See all
                </button>
              </div>
              <div className="setup-grid">
                {proBuilderSetups.map((setup) => (
                  <SetupCard
                    key={setup.id}
                    setup={setup}
                    onInstall={handleInstall}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Free Community Setups */}
          {communitySetups.length > 0 && (
            <section className="marketplace-section">
              <div className="section-header">
                <h2 className="section-title">Free Community Setups</h2>
                <button
                  className="see-all-btn"
                  onClick={() => setFilters((f) => ({ ...f, laneFilter: "community" }))}
                >
                  See all
                </button>
              </div>
              <div className="setup-grid">
                {communitySetups.map((setup) => (
                  <SetupCard
                    key={setup.id}
                    setup={setup}
                    onInstall={handleInstall}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Rising Creators */}
          {risingCreators.length > 0 && (
            <section className="marketplace-section">
              <div className="section-header">
                <h2 className="section-title">Rising Creators</h2>
                <span className="section-subtitle">Based on recent downloads & ratings</span>
              </div>
              <div className="rising-creators-grid">
                {risingCreators.map((creator) => (
                  <div
                    key={creator.name}
                    className="rising-creator-card"
                    onClick={() => creator.slug && navigate(`/marketplace/shop/${creator.slug}`)}
                  >
                    <div className="rising-creator-avatar">
                      {creator.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="rising-creator-info">
                      <span className="rising-creator-name">
                        {creator.name}
                        {creator.isVerified && <span className="rising-verified">✓</span>}
                      </span>
                      <div className="rising-creator-stats">
                        <span>{creator.avgRating.toFixed(1)} ★</span>
                        <span>{creator.totalDownloads} downloads</span>
                        <span>{creator.setupCount} {creator.setupCount === 1 ? "setup" : "setups"}</span>
                      </div>
                    </div>
                    <span className="rising-badge">Rising</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {setups.length === 0 && (
            <div className="empty-state">
              No setups available yet. Check back soon!
            </div>
          )}
        </>
      )}
    </div>
  );
}
